import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { api, errText, useAdminAuth } from "../../lib/admin";
import { Btn, Field, field } from "../../components/admin/ui";

export const AdminFrame = ({ title, sub, children }) => (
  <div className="min-h-screen bg-[#F6F2EC] flex items-center justify-center px-5 py-16" data-testid="admin-auth-page">
    <div className="w-full max-w-md">
      <img src="/images/logo-gioia-dark.png" alt="GIOIA centro estetico" className="h-16 w-auto mx-auto" />
      <div className="mt-10 bg-white border border-[#e7dfd3] rounded-2xl p-7 sm:p-9 shadow-[0_30px_60px_-40px_rgba(20,17,15,0.35)]">
        <p className="text-[0.7rem] tracking-[0.22em] uppercase text-[#8A6A3A]">Area riservata</p>
        <h1 className="mt-2 font-serif-d font-light text-4xl text-[#14110F]">{title}</h1>
        {sub && <p className="mt-2 text-sm text-[#6b625a]">{sub}</p>}
        <div className="mt-7">{children}</div>
      </div>
      <p className="mt-6 text-center text-xs text-[#6b625a]"><Link to="/" className="underline underline-offset-4">Torna al sito</Link></p>
    </div>
  </div>
);

const Password = ({ value, onChange, testId, autoComplete = "current-password", minLength }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input required type={show ? "text" : "password"} minLength={minLength} autoComplete={autoComplete} className={`${field} pr-11`} value={value} onChange={onChange} data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Nascondi password" : "Mostra password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6b625a]" data-testid={`${testId}-toggle`}>
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const Forgot = ({ onBack }) => {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/auth/forgot-password", { email, origin_url: window.location.origin }); setSent(true); } catch (err) { toast.error(errText(err)); }
    setLoading(false);
  };
  if (sent) return (
    <div className="space-y-5" data-testid="forgot-sent">
      <p className="text-[#4f4841]">Se l'email è registrata, riceverai a breve un link per impostare una nuova password (valido 1 ora). Controlla anche la cartella spam.</p>
      <Btn variant="ghost" onClick={onBack} data-testid="forgot-back-btn">Torna all'accesso</Btn>
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
      <Field label="Email"><input required type="email" className={field} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></Field>
      <div className="flex flex-wrap gap-3">
        <Btn type="submit" loading={loading} data-testid="forgot-submit-btn">Invia link</Btn>
        <Btn variant="ghost" onClick={onBack} data-testid="forgot-back-btn">Annulla</Btn>
      </div>
    </form>
  );
};

export function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  useEffect(() => { document.title = "Area riservata · Gioia"; }, []);
  if (user) return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try { await login(f.email, f.password); navigate("/admin/riepilogo", { replace: true }); } catch (e2) { setErr(errText(e2)); }
    setLoading(false);
  };
  return (
    <AdminFrame title={forgot ? "Password dimenticata" : "Accedi"} sub={forgot ? "Ti mandiamo un link via email." : "Prenotazioni, buoni regalo, blog e shop."}>
      {forgot ? <Forgot onBack={() => setForgot(false)} /> : (
        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <Field label="Email"><input required type="email" autoComplete="username" className={field} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-login-email-input" /></Field>
          <Field label="Password"><Password value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testId="admin-login-password-input" /></Field>
          {err && <p className="text-sm text-[#9a3b2b]" data-testid="admin-login-error">{err}</p>}
          <Btn type="submit" loading={loading} className="w-full py-3" data-testid="admin-login-submit-btn">Entra</Btn>
          <button type="button" onClick={() => setForgot(true)} className="block mx-auto text-sm text-[#8A6A3A] underline underline-offset-4" data-testid="admin-forgot-link">Password dimenticata?</button>
        </form>
      )}
    </AdminFrame>
  );
}

export function AdminReset() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [p, setP] = useState({ a: "", b: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (p.a !== p.b) { toast.error("Le due password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password: p.a });
      toast.success("Password aggiornata: ora puoi accedere");
      navigate("/admin/login", { replace: true });
    } catch (err) { toast.error(errText(err)); }
    setLoading(false);
  };
  return (
    <AdminFrame title="Nuova password" sub="Almeno 8 caratteri.">
      <form onSubmit={submit} className="space-y-5" data-testid="admin-reset-form">
        <Field label="Nuova password"><Password minLength={8} autoComplete="new-password" value={p.a} onChange={(e) => setP({ ...p, a: e.target.value })} testId="reset-password-input" /></Field>
        <Field label="Ripeti password"><Password minLength={8} autoComplete="new-password" value={p.b} onChange={(e) => setP({ ...p, b: e.target.value })} testId="reset-password-confirm-input" /></Field>
        <Btn type="submit" loading={loading} className="w-full py-3" data-testid="reset-submit-btn">Salva password</Btn>
      </form>
    </AdminFrame>
  );
}
